import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as PropTypes from 'prop-types';
import List from '@material-ui/core/List';
import makeStyles from '@material-ui/core/styles/makeStyles';
import debounce from 'lodash/debounce';
import { IdPropType } from '../../../proptypes/basic';
import DraftsListItem from './DraftsListItem';
import ResourceWaypoint from '../../table/components/resource/ResourceWaypoint';
import MailDraftFilter from './MailDraftFilter';
import { EMAIL_DRAFT_RESOURCE } from '../../api/resources';
import { useResourceList } from '../../lists/hooks';
import { useCallbackFunc,  } from '../../hooks';
import { usePersistedInitialValues } from '../../form/hooks';
import { MAIL_FOLDER_DRAFTS } from '../constants';

const useStyles = makeStyles(() => ({
    root: {
        height: '100%',
    },
}));

const DraftsList = ({
    listId,
    selected,
    customerId,
    onSelect,
    reloadToggle,
    selectedFolder,
    onChangeFolder,
}) => {
    const classes = useStyles();

    const PERSIST_KEY = useMemo(() => `MailDraftFilter.${customerId}`, [customerId]);
    const initialValues = usePersistedInitialValues(PERSIST_KEY, {
        filterByText: '',
    });

    const [filters, setFilters] = useState({
        ...initialValues,
    });

    const handleSubmit = useCallback(
        (form) => {
            //console.log('DraftsList handleSubmit form =>', form);
            setFilters(form);
            if (onSelect)
                onSelect(null);
        },
        [setFilters, onSelect]
    );

    const fetchParams = useMemo(() => {
        // show only the email_drafts
        //console.log("show only the email_drafts with filters =>", filters);

        const params = {
            orderBy: ['updatedAt', 'desc'],
            filterByFolder: selectedFolder || MAIL_FOLDER_DRAFTS,
            ...(filters?.filterByText?.length > 0 ? { search: filters.filterByText } : {}),
        };
        return params;

    }, [filters, selectedFolder]);

    const check = useCallback(
        () => ({
            check: (item) => {
                if (
                    Array.isArray(filters.filterByEmployee) &&
                    !filters.filterByEmployee.some((el) => {
                        return el === item.employee_id;
                    })
                ) {
                    console.log('do not show:  nicht im employee filter');
                    return false;
                }

                if (
                    Array.isArray(filters.filterByInbox) &&
                    !filters.filterByInbox.some((el) => {
                        return el === item.emailInboxId;
                    })
                ) {
                    console.log('do not show:  nicht im inbox filter');
                    return false;
                }

                return true;
            },
        }),
        [filters]
    );

    const criteria = useMemo(
        () => ({
            check,
        }),
        [check]
    );

    const { handlePage, dataIds, handleNextPage, loading } = useResourceList({
        listId,
        resource: EMAIL_DRAFT_RESOURCE,
        fetchParams,
        criteria,
        autoload: false,
        refresh: true,
        limit: 6,
        continuous: true,
    });

    // Debouncen wir hier mal, damit das Select mit den persisteten werten
    // die Chance hat erstmal alles zu laden, bevor wir hier zwei requests machen
    // eine mit user.id und dann noch einen anderen.
    const debouncedLoadPage = useCallback(debounce(handlePage, 200), [handlePage]);
    const loadPage = useCallbackFunc((page) => {
        if (!loading) {
            debouncedLoadPage(null, page, true);
        }
    });

    useEffect(() => {
        loadPage(1);
    }, [filters, loadPage, reloadToggle]);

    useEffect(() => {
        if (dataIds && dataIds.length > 0) {
            if (selected !==null && onSelect) {
                if (dataIds.includes(selected))
                    onSelect(selected);
                else
                    onSelect(null);
            }
            else {
                onSelect(dataIds[0]);
            }
        }
    }, [selected, dataIds, onSelect]);

    return (
        <List className={classes.root}>
            <MailDraftFilter
                customerId={customerId}
                onSubmit={handleSubmit}
                initialValues={initialValues}
                key={`customer.MailFilter.${customerId}`}
                selectedFolder={selectedFolder}
                onChangeFolder={onChangeFolder}
            />
            {(dataIds || []).map((id) => (
                <DraftsListItem
                    draftId={id}
                    selected={selected === id}
                    onSelect={onSelect}
                    key={`mailItem.${id}`}
                />
            ))}
            <ResourceWaypoint onEnter={handleNextPage} listId={listId} />
        </List>
    );
};

DraftsList.propTypes = {
    listId: PropTypes.string.isRequired,
    selected: IdPropType,
    customerId: IdPropType,
    onSelect: PropTypes.func.isRequired,
    reloadToggle: PropTypes.number,
    selectedFolder: PropTypes.string.isRequired,
    onChangeFolder: PropTypes.func.isRequired,
};

DraftsList.defaultProps = {
    customerId: null,
    selected: null,
    reloadToggle: 0,
};

export default DraftsList;
